/* eslint indent: "off" */
import React from 'react';
import PropTypes from 'prop-types';
import Zone from '../models/zone';

import { ListItem, Overlay } from '../common/common';
import { ZoomControls } from '../common/zoom/zoom';

import './zone-selector.css';
import { ReactComponent as SVGBackButton } from '../images/back_arrow.svg';
import Color from '../utils/color';
import ZoneSelector3d from './zone-selector-3d';
import { SVG, ZonePolygons } from './zone-editor';
import { useTranslation } from 'react-i18next';

function ZoneStyles({ defaultColor }) {
  let lightenColor = Color.fromString(defaultColor).lighten(40).toString();
  return (
    <style>
      {`div.throne-list div.throne-list-item {
            background-color: ${defaultColor};
        }
        div.throne-list div.throne-list-item:hover {
            background-color: ${lightenColor};
        },
        `}
    </style>
  );
}

ZoneStyles.propTypes = {
  defaultColor: PropTypes.string.isRequired,
};

/**
 * LIST <list> Selector
 */
function ListZoneAvailability({ zone, text }) {
  let { availability } = zone;
  if (availability >= ListZoneAvailability.thresholds.AVAILABLE) {
    return null;
  }

  return (
    <div className="throne-zone-list-seats-message">
      {availability}
      {text && ` ${text}`}
    </div>
  );
}

ListZoneAvailability.thresholds = {
  AVAILABLE: 50,
};

ListZoneAvailability.propTypes = {
  zone: PropTypes.instanceOf(Zone),
  text: PropTypes.string,
};

export function generateGradient(colors) {
  let steps = 100 / colors.length;
  let backgroundColor = 'linear-gradient(45deg,';
  let percentage = 0;
  for (let color of colors) {
    backgroundColor += `${color} ${percentage}%, `;
    percentage += steps;
    backgroundColor += `${color} ${percentage}%, `;
  }
  backgroundColor = backgroundColor.replace(/,\s$/, '');
  backgroundColor += ')';
  return backgroundColor;
}

export function ListZoneItem({
  zone,
  onZoneOver,
  onZoneSelected,
  getLabel = (z) => z.name,
  showAvailability = true,
  defaultColor = '#0f32fa',
  unavailableColor = 'gray',
  text = '',
  isSelected = false,
  onChangeSelectedZone = () => {},
}) {
  let onZoneClicked = React.useCallback(() => {
    onZoneSelected(zone);
    onChangeSelectedZone(isSelected ? null : zone.uuid);
  }, [onZoneSelected, onChangeSelectedZone, zone, isSelected]);

  let style = {};

  const { t } = useTranslation();

  // We will soon be able to write this zone.seatlib_data?.color
  if (!zone.isAvailable && unavailableColor !== false) {
    style.background = unavailableColor;
  } else if (zone.colors) {
    style.background = generateGradient(zone.colors);
  }

  const handleMouseOver = React.useCallback(() => {
    let tmpStyle = { ...style };
    if (!zone.isAvailable) {
      return onZoneOver(zone);
    }

    if (zone.colors) {
      tmpStyle.background = zone.colors
        ? generateGradient(zone.colors)
        : defaultColor;
    }
    style = { ...tmpStyle };
    onZoneOver(zone);
  }, [zone, style, onZoneOver, defaultColor]);

  const handleMouseOut = React.useCallback(() => {
    if (!zone.isAvailable) {
      return onZoneOver(null);
    }
    delete style.backgroundColor;
    // passing null means: no zone on over
    onZoneOver(null);
  }, [onZoneOver, zone, style]);
  return (
    <ListItem
      style={style}
      onClick={onZoneClicked}
      className={isSelected ? 'throne-list-item-selected' : ''}
      onMouseEnter={handleMouseOver}
      onMouseLeave={handleMouseOut}
    >
      <span className="throne-list-label">
        {/* translation key handle by willy */}
        {getLabel(zone)}{' '}
        {zone.removed && (
          <span className="throne-label-deleted">
            ({t('ticket.common.deleted')})
          </span>
        )}
      </span>

      {showAvailability && <ListZoneAvailability zone={zone} text={text} />}
    </ListItem>
  );
}

ListZoneItem.propTypes = {
  zone: PropTypes.instanceOf(Zone),
  onZoneOver: PropTypes.func.isRequired,
  onZoneSelected: PropTypes.func.isRequired,
  getLabel: PropTypes.func,
  showAvailability: PropTypes.bool,
  unavailableColor: PropTypes.string,
  defaultColor: PropTypes.string,
  text: PropTypes.string,
  isSelected: PropTypes.bool,
  onChangeSelectedZone: PropTypes.func,
};

export function ListNavigationBack({ children, onClick = () => {} }) {
  return (
    <div className="throne-zone-list-go-back" onClick={onClick}>
      {children}
    </div>
  );
}

ListNavigationBack.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
};

export function ListZoneSelector({
  onZoneOver = () => {},
  getLabel,
  BackButton = SVGBackButton,
  ZoneItem = ListZoneItem,
  showAvailability,
  defaultColor = '#0f32fa',
  unavailableColor = 'gray',
  onZoneClicked,
  displayed_zones,
  can_go_back,
  go_back,
  display_back_button = true,
  text,
}) {
  // zone.getChildren() returns an array of ChildZone(s)

  const [selectedZone, setSelectedZone] = React.useState(null);

  const items = React.useMemo(() => {
    return displayed_zones.map((zone) => (
      <ZoneItem
        defaultColor={defaultColor}
        unavailableColor={unavailableColor}
        key={zone.uuid}
        zone={zone}
        onZoneSelected={onZoneClicked}
        onZoneOver={onZoneOver}
        isSelected={selectedZone === zone.uuid}
        onChangeSelectedZone={setSelectedZone}
        getLabel={getLabel}
        showAvailability={showAvailability}
        text={text}
      />
    ));
  }, [
    defaultColor,
    unavailableColor,
    displayed_zones,
    onZoneClicked,
    onZoneOver,
    getLabel,
    showAvailability,
  ]);

  return (
    <div className="throne-zone-list-container">
      <ZoneStyles defaultColor={defaultColor} />
      <div className={`throne-list ${can_go_back ? 'back-button' : ''}`}>
        {can_go_back && display_back_button && (
          <ListNavigationBack onClick={go_back}>
            <BackButton />
          </ListNavigationBack>
        )}
        {items}
      </div>
    </div>
  );
}

ListZoneSelector.propTypes = {
  zone: PropTypes.instanceOf(Zone).isRequired,
  onZoneOver: PropTypes.func,
  getLabel: PropTypes.func,
  BackButton: PropTypes.node,
  ZoneItem: PropTypes.node,
  showAvailability: PropTypes.bool,
  defaultColor: PropTypes.string,
  onZoneClicked: PropTypes.func,
  displayed_zones: PropTypes.array,
  can_go_back: PropTypes.bool,
  go_back: PropTypes.func,
  display_back_button: PropTypes.bool,
  unavailableColor: PropTypes.string,
  text: PropTypes.string,
};

export function MapZoneSelector({
  zone,
  onZoneOver = () => {},
  onZoneClicked,
  displayed_zones,
  onLoad = () => {},
  enable_zoom = false,
  overlay = null,
}) {
  const contentRef = React.useRef(null);
  const containerRef = React.useRef(null);

  // States to actually set information in transform
  const [currentZoom, setCurrentZoom] = React.useState(1);
  const [last_x_y, setLastXY] = React.useState({ x: 0, y: 0 });

  // Specific states to handle mouse/touch events
  const [in_pan, setInPan] = React.useState(false);
  const [last_touch_x_y, setLastTouchXY] = React.useState({ x: null, y: null });

  // Specific state to handle when a zone must be considered clickable
  // Ex: when finishing a pan event, we do not want to tigger the click
  const zoneClickable = React.useRef(true);

  const onZoneSelected = React.useCallback(
    (zone) => {
      if (!zoneClickable.current) {
        zoneClickable.current = true;
        return;
      }
      onZoneClicked(zone);
    },
    [onZoneClicked],
  );

  const zonePolygons = React.useMemo(() => {
    return displayed_zones.map((zone) => (
      <ZonePolygons
        key={zone.uuid}
        onZoneOver={onZoneOver}
        zone={zone}
        onZoneSelected={onZoneSelected}
      />
    ));
  }, [onZoneSelected, zone, displayed_zones, onZoneOver]);

  const handleTouchStart = React.useCallback(() => {
    if (currentZoom <= 1) {
      return;
    }
    setInPan(true);
  }, [currentZoom]);

  const handleTouchEnd = React.useCallback(() => {
    if (currentZoom <= 1) {
      return;
    }
    setLastTouchXY({ x: null, y: null });
    setInPan(false);
  }, [currentZoom]);

  const handleMouseDown = React.useCallback(() => {
    if (currentZoom <= 1) {
      return;
    }

    setInPan(true);
  }, [currentZoom]);

  const handleMouseUp = React.useCallback(() => {
    if (!in_pan) {
      return;
    }
    setInPan(false);
  }, [in_pan]);

  const { max_pan_x, max_pan_y } = React.useMemo(() => {
    if (currentZoom <= 1) {
      return { max_pan_x: 0, max_pan_y: 0 };
    }
    const { width, height } = containerRef.current.getBoundingClientRect();
    return {
      max_pan_x: (width - width / currentZoom) / 2,
      max_pan_y: (height - height / currentZoom) / 2,
    };
  }, [currentZoom]);

  const pan = React.useCallback(
    ({ deltaX, deltaY }) => {
      let new_last_x =
        deltaX < 0
          ? Math.min(last_x_y.x - deltaX, max_pan_x)
          : Math.max(last_x_y.x - deltaX, -max_pan_x);
      let new_last_y =
        deltaY < 0
          ? Math.min(last_x_y.y - deltaY, max_pan_y)
          : Math.max(last_x_y.y - deltaY, -max_pan_y);
      setLastXY({ x: new_last_x, y: new_last_y });

      const transform = `scale(${currentZoom}) translate(${new_last_x}px, ${new_last_y}px)`;
      contentRef.current.style.transform = transform;
    },
    [currentZoom, last_x_y, contentRef, containerRef, max_pan_x, max_pan_y],
  );

  const zoom = React.useCallback(
    (maginify = true) => {
      let newZoom = currentZoom;

      if (maginify) {
        newZoom = currentZoom * 1.1;
      } else {
        if (currentZoom * 0.95 < 1) {
          newZoom = 1;
        } else {
          newZoom = currentZoom * 0.9;
        }
      }

      const transform = `scale(${newZoom})`;
      contentRef.current.style.transform = transform;

      setCurrentZoom(newZoom);
      setLastXY({ x: 0, y: 0 });
    },
    [currentZoom],
  );

  const handleMouseMove = React.useCallback(
    (e) => {
      if (!in_pan) {
        return;
      }
      zoneClickable.current = false;
      const { movementX, movementY } = e;

      pan({
        deltaX: -movementX,
        deltaY: -movementY,
      });
    },
    [pan, in_pan],
  );

  const handleTouchMove = React.useCallback(
    (e) => {
      if (!in_pan) {
        return;
      }
      e.preventDefault();

      const [touch] = e.touches;
      const { clientX, clientY } = touch;
      const { x, y } = last_touch_x_y;
      if (x === null) {
        setLastTouchXY({ x: clientX, y: clientY });
        return;
      }

      const deltaX = x - clientX;
      const deltaY = y - clientY;

      setLastTouchXY({ x: clientX, y: clientY });
      pan({ deltaX, deltaY });
    },
    [pan, last_touch_x_y, in_pan],
  );

  React.useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    contentRef.current.addEventListener('touchmove', handleTouchMove);

    return () => {
      contentRef.current.removeEventListener('touchmove', handleTouchMove);
    };
  }, [contentRef, handleTouchMove]);

  return (
    <div className="throne-zone-map-container">
      {overlay && <Overlay>{overlay}</Overlay>}
      {enable_zoom ? (
        <>
          <div
            ref={containerRef}
            className="throne-zone-map-with-zoom-container"
          >
            <SVG
              image={zone.preview_picture.url}
              className={`throne-zone-map ${currentZoom > 1 ? 'movable' : ''}`}
              onLoad={onLoad}
              ref={contentRef}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {zonePolygons}
            </SVG>
            <ZoomControls
              className="throne-zone-selector-zoom-controls"
              onZoomIn={zoom}
              onZoomOut={() => zoom(false)}
            />
          </div>
        </>
      ) : (
        <SVG
          image={zone.preview_picture.url}
          className="throne-zone-map"
          onLoad={onLoad}
          ref={contentRef}
        >
          {zonePolygons}
        </SVG>
      )}
    </div>
  );
}

MapZoneSelector.propTypes = {
  zone: PropTypes.instanceOf(Zone).isRequired,
  onZoneOver: PropTypes.func,
  onZoneClicked: PropTypes.func,
  displayed_zones: PropTypes.array,
  onLoad: PropTypes.func,
  enable_zoom: PropTypes.bool,
  overlay: PropTypes.node,
};

const SELECTORS = {
  list: ListZoneSelector,
  map: MapZoneSelector,
};

export function ZoneSelector2d({
  type = 'list',
  zone,
  showImage = false,
  onZoneSelected = () => {},
  onBackButtonClicked,
  showUnavailableZone = true,
  ...props
}) {
  if (!SELECTORS[type]) {
    throw new Error(`[ZoneSelector] No selector named: ${type}`);
  }

  const child_filter = React.useCallback(
    (child) => {
      // if we specifically populate display value
      // we let this value decide if we keep the child
      if (child.display !== undefined) {
        return child.display;
      }
      // Otherwise, if we keep unavailable zone we keep it
      if (showUnavailableZone) {
        return true;
      }
      // Finally we only keep the zone if it's available
      return child.isAvailable;
    },
    [showUnavailableZone],
  );

  const displayed_zones = React.useMemo(() => {
    if (!zone) {
      return [];
    }
    return zone.getChildren().filter(child_filter);
  }, [child_filter, zone]);

  const onZoneClicked = React.useCallback(
    (zone) => {
      let is_final =
        zone.seats &&
        zone.seats.length &&
        (!zone.children || !zone.children.length);
      let is_ga =
        zone.categories.length === 1 &&
        zone.categories[0].contingents.length === 1 &&
        !zone.categories[0].contingents[0].seated;
      onZoneSelected(zone, { final: is_final, ga: is_ga });
    },
    [onZoneSelected],
  );

  const can_go_back = zone.uuid !== 'root';

  const go_back = React.useCallback(() => {
    if (!can_go_back) {
      return;
    }

    onBackButtonClicked(zone);
  }, [can_go_back, onBackButtonClicked, zone]);

  let Component = SELECTORS[type];
  let component_render = (
    <Component
      zone={zone}
      displayed_zones={displayed_zones}
      onZoneClicked={onZoneClicked}
      go_back={go_back}
      can_go_back={can_go_back}
      {...props}
    />
  );

  if (showImage && zone.preview_picture && type !== 'map') {
    return (
      <div className="zone-container">
        <img
          alt={`Zone ${zone.name}`}
          className="zone-image"
          src={zone.preview_picture.url}
        />
        {component_render}
      </div>
    );
  }

  return component_render;
}

ZoneSelector2d.propTypes = {
  type: PropTypes.string,
  zone: PropTypes.instanceOf(Zone).isRequired,
  onZoneSelected: PropTypes.func,
  showImage: PropTypes.bool,
  locale: PropTypes.string,
  onBackButtonClicked: PropTypes.func,
  showUnavailableZone: PropTypes.bool,
};

// Component allowing to decide for us how we render
export function ZoneSelector2dHelper({ zone, type = null, ...rest }) {
  const display_type = React.useMemo(() => {
    // If we specifically asked for a type
    if (type) {
      return type;
    }
    // if we don't have a zone picture we render a list
    if (!zone.preview_picture) {
      return 'list';
    }
    // Let's compute if at least one child got points in its seatlib_data
    return zone.children.some(
      (child) =>
        child.seatlib_data &&
        child.seatlib_data.points &&
        child.seatlib_data.points.length,
    )
      ? 'map'
      : 'list';
  }, [zone, type]);

  return <ZoneSelector2d zone={zone} type={display_type} {...rest} />;
}
ZoneSelector2dHelper.propTypes = {
  zone: PropTypes.object,
  type: PropTypes.string,
};
export function ZoneSelector({ mode3d = false, zone, ...rest }) {
  let zone_model = new Zone(zone);

  if (mode3d) {
    return <ZoneSelector3d zone={zone_model} {...rest} />;
  }
  return <ZoneSelector2dHelper zone={zone_model} {...rest} />;
}

ZoneSelector.propTypes = {
  mode3d: PropTypes.bool,
  zone: PropTypes.object.isRequired,
};
